import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { ChatState } from "../types.js";
import type { ChatHomeTile } from "../home-tiles.js";
import "./jonas-conversations-panel.js";

type ResizableLayout = HTMLElement & { rightWidth: string };

/**
 * Persisted as a ratio (sourceWidth / layoutWidth) so the split scales
 * proportionally across viewport sizes. Until the user drags the divider the
 * panel falls back to DEFAULT_SOURCE_RATIO.
 */
const SOURCE_WIDTH_STORAGE_KEY = "help-jonas:source-panel-width";

/** Initial split used until the user drags the divider for the first time. */
const DEFAULT_SOURCE_RATIO = 0.48;

/** Minimum width of the source panel, mirrored on `right-min-width`. */
const SOURCE_MIN_WIDTH = 360;

/** Maximum width of the source panel, mirrored on `right-max-width`. */
const SOURCE_MAX_WIDTH = 1100;

/** Minimum width the chat column must always keep, mirrored on `content-min-width`. */
const CHAT_MIN_WIDTH = 420;

/** Width of the conversations panel once collapsed; mirrored in jonas-conversations-panel.ts. */
const RAIL_WIDTH = 56;

/** Persisted absolute width (px) of the conversations panel. */
const PANEL_WIDTH_STORAGE_KEY = "help-jonas:conversations-panel-width";

/** Default conversations panel width until the user drags the divider. */
const DEFAULT_PANEL_WIDTH = 280;

/** Conversations panel resize bounds. */
const PANEL_MIN_WIDTH = 240;
const PANEL_MAX_WIDTH = 520;

/**
 * afas-designer-layout shadow roots that already have the print stylesheet
 * injected, so ensureDesignerLayoutPrintStyles adds it exactly once per layout.
 */
const designerLayoutPrintRoots = new WeakSet<ShadowRoot>();

@customElement("help-jonas-chat-shell")
export class HelpJonasChatShell extends LitElement {
  static override styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
      overflow: hidden;
      background: var(--color-background-900);
    }

    .shell-grid {
      display: flex;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .conversations-resizer {
      position: relative;
      z-index: 1;
      flex: 0 0 6px;
      width: 6px;
      /* Straddle the panel border with zero net layout width (mirrors the
         AFAS designer-layout resizer): the negative margins pull the 6px hit
         area 3px into the panel and 3px into the chat column so it stays
         centered on the border instead of adding a gap to the right of it. */
      margin: 0 -3px;
      cursor: col-resize;
      background-color: transparent;
    }

    .conversations-resizer:hover,
    .conversations-resizer:active {
      background-color: var(--color-primary-100);
    }

    .conversations-resizer::after {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 2px;
      height: 24px;
      content: "";
      border-radius: 1px;
      background-color: transparent;
      transform: translate(-50%, -50%);
    }

    .conversations-resizer:active::after {
      background-color: var(--color-primary);
    }

    .shell-main {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
    }

    .shell-main afas-designer-layout {
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
    }

    .Chat-actions.in-sidebar {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    /* Ctrl+P prints the chat thread in place (no JS controller / no staged
       copy). Reflow the shell for paper and hide the shell chrome; the thread
       reflows via its own @media print rules, the conversations panel hides via
       its own :host print rule, and the designer-layout grid is flattened to a
       single full-width column by a print stylesheet injected into its shadow
       (ensureDesignerLayoutPrintStyles). */
    @media print {
      :host {
        height: auto;
        overflow: visible;
      }

      .shell-grid {
        display: block;
        height: auto;
        overflow: visible;
      }

      .shell-main {
        display: block;
      }

      .shell-main afas-designer-layout {
        height: auto;
      }

      .conversations-resizer {
        display: none;
      }
    }
  `;

  @property({ attribute: false })
  accessor state: ChatState | null = null;

  @property({ attribute: false })
  accessor homeTiles: ChatHomeTile[] = [];

  @property({ type: Boolean, attribute: "source-can-go-back" })
  accessor sourceCanGoBack = false;

  @property({ type: Number, attribute: "source-scroll-top" })
  accessor sourceScrollTop = 0;

  // Mirrors the conversations panel's own collapsed flag (announced via
  // `jonas-conversations-collapsed`). While collapsed the panel becomes a fixed
  // rail, so the shell must stop forcing the dragged width and hide the
  // resizer; otherwise the inline width wins over the rail CSS.
  @state()
  private accessor conversationsCollapsed = false;

  // Shell width, tracked so the conversations panel can never squeeze the chat column
  // below CHAT_MIN_WIDTH on a narrow viewport.
  @state()
  private accessor shellWidth = 0;

  // Ratio (0-1) of source-panel width to layout width after the user drags
  // the divider; null keeps the initial DEFAULT_SOURCE_RATIO split.
  private sourceWidth: number | null = null;

  private panelWidth = DEFAULT_PANEL_WIDTH;

  private panelResizeStartX = 0;

  private panelResizeStartWidth = 0;

  private panelResizeNudge = 0;

  private lastLayoutWidth = 0;

  private layoutResizeObserver: ResizeObserver | null = null;

  private shellResizeObserver: ResizeObserver | null = null;

  // Tracks whether the source panel is currently mounted so the next mount can
  // be recognised as a fresh open rather than a re-render or source switch.
  private sourceWasActive = false;

  // Set by the app immediately before a live, user-initiated source open. The
  // next panel mount then slides in; mounts from refresh/SSR, history restore,
  // or a source switch leave this false and appear without animating.
  private pendingSourceEntrance = false;

  protected override render() {
    return html`
      <div class="shell-grid">
        <help-jonas-conversations-panel
          .state=${this.state}
          style=${this.conversationsCollapsed
            ? nothing
            : `width:${this.effectivePanelWidth}px`}
          @jonas-conversations-collapsed=${this.handleConversationsCollapsed}
        >
        </help-jonas-conversations-panel>
        ${this.conversationsCollapsed
          ? nothing
          : html`<div
              class="conversations-resizer"
              @mousedown=${this.handlePanelResizeStart}
            ></div>`}
        <div class="shell-main">
          <afas-designer-layout
            right-resizable
            right-width="min(48vw, 820px)"
            right-min-width=${SOURCE_MIN_WIDTH}
            right-max-width=${SOURCE_MAX_WIDTH}
            content-min-width=${this.effectiveChatMinWidth}
            @mousedown=${this.handleResizeStart}
          >
            <help-jonas-chat-thread
              .state=${this.state}
              .homeTiles=${this.homeTiles}
            ></help-jonas-chat-thread>
            ${this.state?.activeSource
              ? html`<help-jonas-source-panel
                  slot="right-sidebar"
                  .state=${this.state}
                  .canGoBack=${this.sourceCanGoBack}
                  .scrollTopOffset=${this.sourceScrollTop}
                >
                  <slot name="source" slot="document"></slot>
                </help-jonas-source-panel>`
              : null}
          </afas-designer-layout>
        </div>
      </div>
    `;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Seed the persisted panel width before the first paint so the conversations
    // panel renders at its stored width immediately — including while the history
    // skeleton shows — instead of flashing DEFAULT_PANEL_WIDTH. firstUpdated runs
    // after the first paint and `panelWidth` is a plain (non-reactive) field, so
    // reading it there left the very first render at the default until the next
    // state-driven re-render (e.g. when history finished loading).
    this.panelWidth = this.readStoredPanelWidth();
  }

  protected override firstUpdated(): void {
    this.ensureDesignerLayoutPrintStyles();
    this.sourceWidth = this.readStoredSourceWidth();
    this.applySourceWidth();
    this.observeLayoutWidth();
    this.observeShellWidth();
  }

  // Called by the app right before a live source open so the resulting panel
  // mount slides in. It is a one-shot intent consumed by the next mount.
  requestSourceEntrance(): void {
    this.pendingSourceEntrance = true;
  }

  protected override updated(): void {
    this.ensureDesignerLayoutPrintStyles();
    const isActive = Boolean(this.state?.activeSource);
    if (isActive && !this.sourceWasActive) {
      // The panel just mounted. Only animate when the open was flagged live;
      // refresh/SSR, history restore, and switches reach here without the flag.
      if (this.pendingSourceEntrance) {
        void this.renderRoot
          .querySelector("help-jonas-source-panel")
          ?.playEntrance();
      }
      this.pendingSourceEntrance = false;
    } else if (!isActive && this.sourceWasActive) {
      // The panel unmounted (close); drop any stale intent so a later
      // history-driven reopen does not inherit it.
      this.pendingSourceEntrance = false;
    }
    this.sourceWasActive = isActive;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.layoutResizeObserver?.disconnect();
    this.layoutResizeObserver = null;
    this.shellResizeObserver?.disconnect();
    this.shellResizeObserver = null;
    window.removeEventListener("mouseup", this.handleResizeEnd);
    window.removeEventListener("mousemove", this.handlePanelResizeMove);
    window.removeEventListener("mouseup", this.handlePanelResizeEnd);
    if (this.panelResizeNudge) {
      window.cancelAnimationFrame(this.panelResizeNudge);
      this.panelResizeNudge = 0;
    }
  }

  // The afas-designer-layout lays its content + source panel out in a grid in
  // its OWN (third-party) shadow root: the right sidebar is pinned to a fixed
  // inline width and the content area is an overflow:auto grid cell. On paper
  // that reserves the (hidden) source column and clips the thread to one page,
  // and we cannot reach those from here (only its `overlay` part is exposed). So
  // inject a print-only sheet into its shadow that flattens the grid to a single
  // full-width column and drops the sidebar/resizer/toolbar. Mirrors the
  // afas-message shadow-print injection in jonas-chat-message.ts. (Article
  // printing does NOT use this path — it clones the article to a top-level
  // container; see printArticle in jonas-source-panel.ts.)
  private ensureDesignerLayoutPrintStyles(): void {
    const layoutRoot =
      this.renderRoot.querySelector("afas-designer-layout")?.shadowRoot;
    if (!layoutRoot || designerLayoutPrintRoots.has(layoutRoot)) return;
    if (
      typeof CSSStyleSheet === "undefined" ||
      !("adoptedStyleSheets" in layoutRoot)
    ) {
      return;
    }
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(
        `@media print {
          .designer-layout { display: block !important; overflow: visible !important; }
          .center { display: block !important; }
          .content-area { overflow: visible !important; min-width: 0 !important; }
          .right-sidebar, .resizer, .toolbar-area { display: none !important; }
        }`,
      );
      layoutRoot.adoptedStyleSheets = [...layoutRoot.adoptedStyleSheets, sheet];
      designerLayoutPrintRoots.add(layoutRoot);
    } catch {
      // Best-effort; without the injection the print falls back to the grid.
    }
  }

  private get layoutElement(): ResizableLayout | null {
    return (
      this.renderRoot.querySelector<ResizableLayout>("afas-designer-layout") ??
      null
    );
  }

  /**
   * Keeps the source panel pinned to its absolute pixel width whenever the
   * available layout width changes (viewport or conversations-panel resize),
   * only shrinking it when the chat column would otherwise drop below
   * `CHAT_MIN_WIDTH`.
   */
  private observeLayoutWidth(): void {
    const layout = this.layoutElement;
    if (!layout || typeof ResizeObserver === "undefined") {
      return;
    }
    this.layoutResizeObserver = new ResizeObserver(() => {
      const layoutWidth = this.layoutElement?.clientWidth ?? 0;
      if (layoutWidth <= 0 || layoutWidth === this.lastLayoutWidth) {
        return;
      }
      this.applySourceWidth();
    });
    this.layoutResizeObserver.observe(layout);
  }

  // Observes the shell itself rather than the chat column: the column's width is a
  // result of the panel width, so clamping against it would feed back on itself.
  private observeShellWidth(): void {
    if (typeof ResizeObserver === "undefined") {
      this.shellWidth = this.clientWidth;
      return;
    }
    this.shellResizeObserver = new ResizeObserver(() => {
      this.shellWidth = this.clientWidth;
    });
    this.shellResizeObserver.observe(this);
  }

  /**
   * The dragged width, capped so the chat column keeps CHAT_MIN_WIDTH. Narrower viewports
   * collapse the panel to its rail, which the panel decides for itself.
   */
  private get effectivePanelWidth(): number {
    if (this.shellWidth <= 0) {
      return this.panelWidth;
    }
    const available = this.shellWidth - CHAT_MIN_WIDTH;
    return Math.max(PANEL_MIN_WIDTH, Math.min(this.panelWidth, available));
  }

  /**
   * CHAT_MIN_WIDTH keeps the source panel from squeezing the chat, but on a phone there
   * simply is not that much room. The layout clips what does not fit, so asking for more
   * than the viewport has puts the send button and the notice's close button off screen
   * with no way to scroll to them. Never ask for more than there is.
   */
  private get effectiveChatMinWidth(): number {
    if (this.shellWidth <= 0) {
      return CHAT_MIN_WIDTH;
    }
    const panel = this.conversationsCollapsed
      ? RAIL_WIDTH
      : this.effectivePanelWidth;
    return Math.max(0, Math.min(CHAT_MIN_WIDTH, this.shellWidth - panel));
  }

  private handleResizeStart = (): void => {
    // The AFAS layout drives the drag internally and exposes no resize event,
    // so we listen for the drag to finish and then read the resulting width.
    window.addEventListener("mouseup", this.handleResizeEnd, { once: true });
  };

  private handleResizeEnd = (): void => {
    const layout = this.layoutElement;
    if (!layout) {
      return;
    }
    const layoutWidth = layout.clientWidth;
    const sourceWidth = this.measureSourceWidth();
    if (layoutWidth <= 0 || sourceWidth <= 0) {
      return;
    }
    const clamped = this.clampSourceWidth(sourceWidth, layoutWidth);
    const ratio = sourceWidth / layoutWidth;
    if (this.sourceWidth !== null && Math.abs(ratio - this.sourceWidth) < 0.001) {
      return;
    }
    this.sourceWidth = ratio;
    this.lastLayoutWidth = layoutWidth;
    this.storeSourceWidth(ratio);
    // Keep the layout's own property in sync with the dragged width so later
    // viewport-driven updates always diff against the real current width.
    layout.rightWidth = `${clamped}px`;
  };

  private measureSourceWidth(): number {
    const panel = this.renderRoot.querySelector<HTMLElement>(
      "help-jonas-source-panel",
    );
    return panel?.offsetWidth ?? 0;
  }

  private applySourceWidth(): void {
    const layout = this.layoutElement;
    if (!layout) {
      return;
    }
    const layoutWidth = layout.clientWidth;
    if (layoutWidth <= 0) {
      return;
    }
    this.lastLayoutWidth = layoutWidth;
    const ratio = this.sourceWidth ?? DEFAULT_SOURCE_RATIO;
    const target = Math.round(ratio * layoutWidth);
    layout.rightWidth = `${this.clampSourceWidth(target, layoutWidth)}px`;
  }

  /**
   * Clamps an absolute source width so the panel stays within its own
   * min/max bounds and the chat column always keeps at least `CHAT_MIN_WIDTH`.
   */
  private clampSourceWidth(width: number, layoutWidth: number): number {
    const maxForChat = layoutWidth - CHAT_MIN_WIDTH;
    const upper = Math.min(
      SOURCE_MAX_WIDTH,
      Math.max(SOURCE_MIN_WIDTH, maxForChat),
    );
    return Math.round(Math.min(upper, Math.max(SOURCE_MIN_WIDTH, width)));
  }

  private readStoredSourceWidth(): number | null {
    try {
      const raw = window.localStorage.getItem(SOURCE_WIDTH_STORAGE_KEY);
      if (raw === null) {
        return null;
      }
      const ratio = Number.parseFloat(raw);
      if (!Number.isFinite(ratio) || ratio <= 0 || ratio >= 1) {
        return null;
      }
      return ratio;
    } catch {
      return null;
    }
  }

  private storeSourceWidth(ratio: number): void {
    try {
      window.localStorage.setItem(SOURCE_WIDTH_STORAGE_KEY, String(ratio));
    } catch {
      // Ignore storage failures (private mode, quota, disabled storage).
    }
  }

  private handleConversationsCollapsed = (event: Event): void => {
    const detail = (event as CustomEvent<{ collapsed?: boolean }>).detail;
    this.conversationsCollapsed = detail?.collapsed === true;
  };

  private handlePanelResizeStart = (event: MouseEvent): void => {
    event.preventDefault();
    this.panelResizeStartX = event.clientX;
    this.panelResizeStartWidth = this.panelWidth;
    window.addEventListener("mousemove", this.handlePanelResizeMove);
    window.addEventListener("mouseup", this.handlePanelResizeEnd, { once: true });
  };

  private handlePanelResizeMove = (event: MouseEvent): void => {
    const delta = event.clientX - this.panelResizeStartX;
    const next = Math.min(
      PANEL_MAX_WIDTH,
      Math.max(PANEL_MIN_WIDTH, this.panelResizeStartWidth + delta),
    );
    if (next !== this.panelWidth) {
      this.panelWidth = next;
      this.requestUpdate();
      // afas-menu/menu-item only relayout their label ellipsis on a window
      // resize, not on container resize, so nudge them after the new width.
      this.notifyPanelResized();
    }
  };

  private handlePanelResizeEnd = (): void => {
    window.removeEventListener("mousemove", this.handlePanelResizeMove);
    this.storePanelWidth(this.panelWidth);
    this.notifyPanelResized();
  };

  // Coalesce repeated resize nudges to one per frame so the menu re-measures
  // without thrashing layout while the divider is dragged.
  private notifyPanelResized(): void {
    if (this.panelResizeNudge) {
      return;
    }
    this.panelResizeNudge = window.requestAnimationFrame(() => {
      this.panelResizeNudge = 0;
      window.dispatchEvent(new Event("resize"));
    });
  }

  private readStoredPanelWidth(): number {
    try {
      const raw = window.localStorage.getItem(PANEL_WIDTH_STORAGE_KEY);
      if (raw === null) {
        return DEFAULT_PANEL_WIDTH;
      }
      const value = Number.parseInt(raw, 10);
      if (!Number.isFinite(value)) {
        return DEFAULT_PANEL_WIDTH;
      }
      return Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, value));
    } catch {
      return DEFAULT_PANEL_WIDTH;
    }
  }

  private storePanelWidth(width: number): void {
    try {
      window.localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(Math.round(width)));
    } catch {
      // Ignore storage failures (private mode, quota, disabled storage).
    }
  }

}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-chat-shell": HelpJonasChatShell;
  }
}
